import React, { useMemo } from 'react';
import { Grid, Flex, Text, Box, Icon, Spinner } from '@chakra-ui/react';
import RateSourceTooltip from '../shared/RateSourceTooltip';
import { FiArrowUpRight, FiArrowDownRight } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../../utils/formatters';
import { cardStyle } from './dashboardStyles';
import UnconvertibleNote from '../shared/UnconvertibleNote';
import Figure from '../shared/Figure';

// A change is tinted by whether it is good news, not by its direction.
const TONE = {
  good: { color: 'redesign.amount.income', bg: 'rgba(21, 128, 61, 0.10)', _dark: { bg: 'rgba(74, 222, 128, 0.14)' } },
  bad: { color: 'redesign.accent.red', bg: 'rgba(185, 28, 28, 0.08)', _dark: { bg: 'rgba(248, 113, 113, 0.14)' } },
};

/**
 * The month in one strip: Income, Expenses, Net balance, side by side on one
 * card. Supports multiple currencies when "all" is selected.
 */
export default function DashboardSummaryCards({ expenses, income, net, selectedCurrency, currencyDeltas, converted, conversions, convertedLoading, displayCurrency }) {
  const { t } = useTranslation('dashboard');
  // MI-298: when a display currency is picked, show one approximate converted
  // value per card (income/expenses/net) instead of the per-currency breakdown.
  const converting = Boolean(displayCurrency);
  const currencies = useMemo(() => {
    const all = new Set([...Object.keys(expenses), ...Object.keys(income), ...Object.keys(net)]);
    return selectedCurrency === 'all'
      ? [...all]
      : all.has(selectedCurrency) ? [selectedCurrency] : [];
  }, [expenses, income, net, selectedCurrency]);

  const size = selectedCurrency === 'all' && currencies.length > 1 ? 'sm' : 'md';

  const getNetDelta = (cur) => {
    const deltas = currencyDeltas?.[cur];
    if (!deltas) return null;
    const { previousIncome, previousExpenses } = deltas;
    if (previousIncome == null && previousExpenses == null) return null;
    const curIncome = income[cur] || 0;
    const curExpenses = expenses[cur] || 0;
    const prevNet = (previousIncome ?? 0) - (previousExpenses ?? 0);
    const curNet = curIncome - curExpenses;
    if (Math.abs(prevNet) < 0.01) return null;
    return Math.round(((curNet - prevNet) / Math.abs(prevNet)) * 10000) / 100;
  };

  // Money in is green; money out and the net stay in ink.
  const cells = [
    { label: t('summaryCards.totalIncome'), values: income, convertedKey: 'income', color: 'redesign.amount.income', getDelta: () => null },
    { label: t('summaryCards.totalExpenses'), values: expenses, convertedKey: 'expenses', color: 'redesign.amount.expense', getDelta: () => null },
    { label: t('summaryCards.netBalance'), values: net, convertedKey: 'total', color: 'redesign.heading', getDelta: cur => getNetDelta(cur), deltaInvert: false },
  ];

  // Grid uses minmax(0, 1fr) instead of 1fr: prevents long values from pushing
  // columns past the grid, which would otherwise cause overflow on narrow viewports.
  return (
    <Box mb={4}>
    <Grid {...cardStyle} p={0} templateColumns="repeat(3, minmax(0, 1fr))" overflow="hidden">
      {cells.map((cell, i) => (
        <Box
          key={cell.label}
          minW={0}
          px={{ base: 3, md: 5 }}
          py={{ base: 3, md: 4 }}
          borderRight={i < cells.length - 1 ? '1px solid' : 'none'}
          borderColor="redesign.border"
        >
          <Text textStyle="microLabel" mb={1}>{cell.label}</Text>
          {converting ? (
            convertedLoading ? (
              <Spinner size="xs" color="redesign.text.muted" />
            ) : converted ? (
              <RateSourceTooltip
                label={t('common:convert.approxTooltip')}
                sources={conversions?.[cell.convertedKey]?.rateSources}
                isDisabled={!conversions?.[cell.convertedKey]?.isConverted}
              >
                <Flex align="baseline" gap={1} cursor="default" wrap="wrap">
                  {conversions?.[cell.convertedKey]?.isConverted && (
                    <Text as="span" color="redesign.text.muted" fontSize="md">≈</Text>
                  )}
                  <Figure size="md" code={displayCurrency} amount={formatMoney(converted[cell.convertedKey] || 0, displayCurrency, true)} color={cell.color} />
                </Flex>
              </RateSourceTooltip>
            ) : (
              <Figure size={size} amount="—" color={cell.color} />
            )
          ) : (
            <>
              {currencies.map(cur => {
                const delta = cell.getDelta(cur);
                const good = delta !== null && delta !== undefined && (cell.deltaInvert ? delta < 0 : delta > 0);
                return (
                  <Flex key={cur} align="center" gap={2} mb="1px" wrap="wrap">
                    <Figure size={size} code={cur} amount={formatMoney(cell.values[cur] || 0, cur, true)} color={cell.color} />
                    {delta !== null && delta !== undefined && (
                      <Flex align="center" gap="2px" flexShrink={0} h="20px" px="6px" borderRadius="6px" {...TONE[good ? 'good' : 'bad']}>
                        <Icon as={delta > 0 ? FiArrowUpRight : FiArrowDownRight} w="11px" h="11px" />
                        <Text fontSize="2xs" fontWeight={600} color="inherit" textStyle="tabularNumbers">
                          {delta > 0 ? '+' : ''}{delta}%
                        </Text>
                      </Flex>
                    )}
                  </Flex>
                );
              })}
              {currencies.length === 0 && (
                <Figure size={size} amount="$0.00" color={cell.color} />
              )}
            </>
          )}
        </Box>
      ))}
    </Grid>
    {converting && converted?.num_unconvertible > 0 && (
      <Box mt={2}>
        <UnconvertibleNote
          buckets={converted.unconvertible}
          count={converted.num_unconvertible}
        />
      </Box>
    )}
    </Box>
  );
}
